<template>
  <div class="pending-universe">
    <div class="actions-bar">
      <div class="pending-title">
          <p>
            Please see below for changes to fund status and fund series ID.
          </p>
          <p v-if="this.userRole === 'FIDUCIARYSERVICES_OPERATIONS'">
            Make sure to scroll to see all the changes before submitting to production.
          </p>
      </div>
      <div class="action-row">
        <mds-button v-if="localPolicyOptions.options['submit-to-production']"
          id="submit-to-production" variation="primary"
          :disabled="!(itemsSelected !== 0)"
          @click="openSubmitToProductionModal"
        >
          Submit to Production
        </mds-button>
        <button
          id="export-pending-view"
          class="mds-button mds-button--icon-only exportButton"
          type="button"
          @click="openExportModal"
          :disabled="!localPolicyOptions.options.export"
          @mouseover="onShowTooltip($event, ['Export'], 'bottom-right', 'small')"
          @mouseleave="onHideTooltip()"
        >
          <svg class="mds-icon mds-button__icon" aria-hidden="true">
            <use
              xmlns:xlink="http://www.w3.org/1999/xlink"
              xlink:href="static/mds/icons/mds.svg#share"
            ></use>
          </svg>
        </button>
        <button
          id="undo-changes-pending-view"
          class="mds-button mds-button--icon-only"
          type="button"
          @click="redirectToGlobalUndoPage"
          @mouseover="onShowTooltip($event, ['Undo Changes'], 'bottom-left', 'medium')"
          @mouseleave="onHideTooltip()"
        >
          <svg class="mds-icon mds-button__icon" aria-hidden="true">
            <use
              xmlns:xlink="http://www.w3.org/1999/xlink"
              xlink:href="static/mds/icons/mds.svg#undo"
            ></use>
          </svg>
        </button>
      </div>
    </div>

    <div class="grid-wrapper"
      id="pending-status-grid-wrapper"
      :class="{
          'no-scrolled': !horizontallyScrolled,
          'scrolled': horizontallyScrolled,
          'scroll-ends': horizontallyScrollEnds || isListEmpty,
        }"
    >
      <PendingStatusChangesGrid
        @showToolTip="onShowTooltip"
        @hideToolTip="onHideTooltip"
      />
    </div>

    <portal-target name="pending-status-paginator-portal">
      <!-- portal -->
    </portal-target>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapState } from 'vuex';
import { EventBus } from '@/utils/event-bus';
import '@mds/typography';
import '@mds/constants';
import PendingStatusChangesGrid from '@/components/organisms/PendingStatusChangesGrid';

export default {
  name: 'pending-universe',
  components: {
    PendingStatusChangesGrid,
  },
  data() {
    return {
      scrolled: false,
      horizontallyScrolled: false,
      horizontallyScrollEnds: false,
      gridRef: null,
    };
  },
  created() {
    this.$store.dispatch('setPendingItemsSelected', {
      list: [],
      total: 0,
    });
    this.$store.dispatch('closeNotification');
    EventBus.$emit('search-clear');
  },
  mounted() {
    this.gridRef = document.getElementById('pending-status-grid-wrapper');
    this.gridRef && this.gridRef.addEventListener('scroll', this.handleScroll);
  },
  methods: {
    onShowTooltip(event, status, orientation, size) {
      this.$emit('showTooltip', event, status, orientation, size);
    },
    onHideTooltip(event) {
      this.$emit('hideTooltip', event);
    },
    redirectToGlobalUndoPage() {
      this.onHideTooltip();
      if (+this.selectedClient !== 0) {
        this.$router.push({ name: 'pendingFundsUndoChanges', params: { selectedClientId: this.selectedClient } });
      } else {
        this.$router.push({ name: 'pendingFundsUndoChanges', params: { selectedClientId: 'all' } });
      }
    },
    openSubmitToProductionModal() {
      /** @component components/molecules/SubmitChange */
      this.$store.dispatch('openModal', {
        title: 'Submit Fund Changes',
        component: 'submit-changes',
        data: {
          selectedFunds: this.itemsSelectedList,
          statusChanges: this.statusChanges,
          seriesChanges: this.seriesChanges,
          categoryChanges: this.categoryChanges,
        },
      });
    },
    openExportModal() {
      this.$store.dispatch('openModal', {
        title: 'Export',
        component: 'export-pending-data',
        data: {},
      });
    },
    handleScroll(event) {
      const grid = event.currentTarget;
      if (grid.scrollLeft && grid.scrollLeft > 20) {
        this.horizontallyScrolled = true;
        if ((grid.clientWidth + grid.scrollLeft) >= grid.scrollWidth) {
          this.horizontallyScrollEnds = true;
        }
        return true;
      }
      this.horizontallyScrollEnds = false;
      this.horizontallyScrolled = false;
      return false;
    },
  },
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      selectedClient: (state) => state.pending.client.selected,
      selectedClientName: (state) => state.pending.client.name,
      searchTerm: (state) => state.pending.searchTerm,
      itemsDisplayed: (state) => state.pending.itemsDisplayed,
      itemsSelected: (state) => state.pending.itemsSelected.total,
      itemsSelectedList: (state) => state.pending.itemsSelected.list,
      statusChanges: (state) => state.pending.itemsSelected.statusChanges,
      seriesChanges: (state) => state.pending.itemsSelected.seriesChanges,
      categoryChanges: (state) => state.pending.itemsSelected.categoryChanges,
      selectedTab: (state) => state.pending.tab,
      userRole: (state) => state.user.userRole,
    }),
    currentViewName() {
      return this.selectedClient > 0 ? 'one-client' : 'all-clients';
    },
    localPolicyOptions() {
      // Tabs are applied to both Views
      return get(this.policies, `pending.views.${this.currentViewName}.tabs.${this.selectedTab}`);
    },
    isListEmpty() {
      return this.itemsDisplayed === 0;
    },
  },
  beforeDestroy() {
    this.gridRef && this.gridRef.removeEventListener('scroll', this.handleScroll);
  },
};
</script>

<style lang="scss">
.pending-universe {
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  .pending-title {
    padding: 0 $mds-space-2-and-a-half-x;
    p {
      @include mds-level-6-heading();
      margin: $mds-space-three-quarter-x 0;
    }
  }

  .grid-wrapper {
    @include vertical-scroll-gradient();
    height: calc(100% - 61px);
    width: calc(100vw - 85px - 120px);
    overflow-y: auto;
    overflow-x: auto;

    &:after {
      content: "";
      position: absolute;
      background:
        linear-gradient(
          to right,
          rgba(0,0,0,0) 1%,
          rgba(70,70,70,0) 59%,
          rgba(200,200,200,0.4) 100%
        ),
        radial-gradient(
          farthest-side at 100% 50%,
          rgba(200, 200, 200, 0.7),
          rgba(200, 200, 200,0)
        ) 0 100%;
      width: 26px;
      right: 15px;
      top: 120px;
      bottom: 76px;
      border-right: 1px solid rgba(100, 100, 100, 0.5);
      z-index: 101;
    }

    &.scroll-ends {
      &:after {
        display: none;
      }
    }

    &.scrolled {
      &:before {
        content: "";
        position: absolute;
        background:
          linear-gradient(
            to right,
            rgba(200, 200, 200, 0.7) 1%,
            rgba(70, 70, 70, 0) 59%,
            rgba(0, 0, 0, 0) 100%
          ),
          radial-gradient(
            farthest-side at 0% 50%,
            rgba(200, 200, 200, 0.5),
            rgba(200, 200, 200,0)
          ) 0 100%;
        width: 26px;
        left: 0;
        top: 120px;
        bottom: 76px;
        border-left: 1px solid rgba(100, 100, 100, 0.5);
        z-index: 101;
      }
    }
  }
}

.mds-loader {
  width: 100%;
}

.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: $mds-space-2-x;
  font-size: $mds-font-size-s;
  margin: $mds-space-2-x 0;

  .rowCount {
    font-weight: $mds-font-weight-light;
    margin: 0 $mds-space-4-x;
    flex-grow: 1;
  }

  button {
    margin-left: $mds-space-inset-2-x;
  }
}
</style>
